import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { CITIES, CATEGORIES, CATEGORY_ICONS, DISTRICTS, MAX_MERCHANT_CATEGORIES, FULL_ACTIVITIES, VET_ACTIVITIES, MERCHANT_TYPES } from "@/lib/constants";
import { errMsg } from "@/lib/api";
import GoogleButton from "@/components/GoogleButton";
import Logo from "@/components/Logo";
import { isValidSaudi, SAUDI_HINT_EN, SAUDI_HINT_AR } from "@/lib/phone";
import { toast } from "sonner";

const inputCls = "w-full h-12 rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-4";

export default function Signup() {
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "merchant" ? "merchant" : "customer";
  const { register } = useAuth();
  const { t, tCat, tAct, tCity } = useLang();
  const navigate = useNavigate();
  const [role, setRole] = useState(initialRole);
  const [form, setForm] = useState({
    email: "", password: "", name: "", phone: "",
    business_name: "", city: "", categories: [], activities: [], whatsapp: "", description: "",
    coverage: "full_city", districts: [], merchant_type: "",
  });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const cityDistricts = DISTRICTS[form.city] || [];

  const toggleCat = (c) => {
    setForm((f) => {
      if (f.categories.includes(c)) {
        const catActs = FULL_ACTIVITIES[c] || [];
        return {
          ...f,
          categories: f.categories.filter((x) => x !== c),
          activities: f.activities.filter((a) => !catActs.includes(a)),
        };
      }
      if (f.categories.length >= MAX_MERCHANT_CATEGORIES) {
        toast.error(t("maxTwoCategories"));
        return f;
      }
      return { ...f, categories: [...f.categories, c] };
    });
  };

  const toggleActivity = (a) => {
    setForm((f) => ({
      ...f,
      activities: f.activities.includes(a) ? f.activities.filter((x) => x !== a) : [...f.activities, a],
    }));
  };

  const toggleDistrict = (d) => {
    setForm((f) => ({
      ...f,
      districts: f.districts.includes(d) ? f.districts.filter((x) => x !== d) : [...f.districts, d],
    }));
  };

  const canSubmit = useMemo(() => {
    if (!form.email || !form.password || !form.name) return false;
    if (form.phone && !isValidSaudi(form.phone)) return false;
    if (role === "merchant") {
      if (!form.business_name || !form.city || form.categories.length === 0 || !form.whatsapp) return false;
      if (form.categories.length > MAX_MERCHANT_CATEGORIES) return false;
      if (!isValidSaudi(form.whatsapp)) return false;
      if (form.coverage === "districts" && form.districts.length === 0) return false;
      // Every selected category needs at least one specific service
      for (const c of form.categories) {
        const catActs = FULL_ACTIVITIES[c] || [];
        if (!form.activities.some((a) => catActs.includes(a))) return false;
      }
    }
    return true;
  }, [form, role]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    const payload = { ...form, role };
    if (role === "customer") {
      delete payload.business_name; delete payload.city; delete payload.categories;
      delete payload.whatsapp; delete payload.description; delete payload.coverage; delete payload.districts;
      delete payload.activities; delete payload.merchant_type;
    } else if (!payload.merchant_type) {
      delete payload.merchant_type;
    }
    try {
      const u = await register(payload);
      if (u.role === "customer") navigate("/customer");
      else navigate("/merchant");
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-shell p-5 pb-24" data-testid="signup-page">
      <div className="flex items-center justify-between">
        <Logo className="h-9 w-auto" testId="signup-logo" />
        <Link to="/" className="text-sm text-zinc-500 hover:text-zinc-900" data-testid="signup-back">{t("backHome")}</Link>
      </div>
      <h1 className="font-display text-3xl font-extrabold mt-6 text-[#12211D]">{t("createAccount")}</h1>
      <p className="text-zinc-600 mt-1 text-sm">
        {role === "customer" ? t("asCustomer") : t("asMerchant")}
      </p>

      <div className="mt-5 grid grid-cols-2 gap-2 rounded-full bg-zinc-100 p-1">
        <button
          data-testid="role-customer"
          type="button"
          onClick={() => setRole("customer")}
          className={`rounded-full py-2 text-sm font-semibold transition ${role === "customer" ? "bg-white shadow text-emerald-700" : "text-zinc-600"}`}
        >{t("customer")}</button>
        <button
          data-testid="role-merchant"
          type="button"
          onClick={() => setRole("merchant")}
          className={`rounded-full py-2 text-sm font-semibold transition ${role === "merchant" ? "bg-white shadow text-emerald-700" : "text-zinc-600"}`}
        >{t("merchant")}</button>
      </div>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <Field label={t("name")} testId="signup-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
        <Field label={t("email")} type="email" testId="signup-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
        <Field label={t("password")} type="password" testId="signup-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
        <PhoneField
          label={t("phone")}
          testId="signup-phone"
          value={form.phone}
          onChange={(v) => setForm({ ...form, phone: v })}
          required={role === "merchant"}
          lang={t("ar") === "ع" ? "ar" : "en"}
        />

        {role === "merchant" && (
          <>
            <Field label={t("businessName")} testId="signup-business" value={form.business_name} onChange={(v) => setForm({ ...form, business_name: v })} />
            <div>
              <label className="label-caps mb-1.5 block">{t("city")}</label>
              <select
                data-testid="signup-city"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value, districts: [] })}
                className={inputCls.replace("px-4", "px-3")}
              >
                <option value="">{t("selectCity")}</option>
                {CITIES.map((c) => <option key={c} value={c}>{tCity(c)}</option>)}
              </select>
            </div>

            <div>
              <label className="label-caps mb-2 block">{t("selectCategories")}</label>
              <p className="text-xs text-zinc-500 mb-2">{t("maxTwoCategories")}</p>
              <div className="grid grid-cols-2 gap-2">
                {CATEGORIES.map((c) => {
                  const Icon = CATEGORY_ICONS[c];
                  const sel = form.categories.includes(c);
                  const disabled = !sel && form.categories.length >= MAX_MERCHANT_CATEGORIES;
                  return (
                    <button
                      type="button"
                      key={c}
                      data-testid={`signup-cat-${c}`}
                      onClick={() => toggleCat(c)}
                      className={`flex items-center gap-2 rounded-xl border px-3 py-2.5 text-xs font-medium transition ${
                        sel ? "border-emerald-600 bg-emerald-50 text-emerald-700" : disabled ? "border-zinc-100 bg-zinc-50 text-zinc-400" : "border-zinc-200 bg-white text-zinc-700 hover:border-zinc-300"
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span>{tCat(c)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Specific services per selected category (complete lists) */}
            {form.categories.length > 0 && (
              <div data-testid="signup-activities-section">
                <label className="label-caps mb-1 block">{t("myActivitiesTitle")}</label>
                <p className="text-xs text-zinc-500 mb-2">{t("selectActivitiesPerCategory")}</p>
                {form.categories.includes("pets") && (
                  <p className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-[11px] font-medium px-3 py-2 mb-2" data-testid="vet-notice-signup">
                    {t("vetNotice")}
                  </p>
                )}
                {form.categories.includes("moving_transport") && (
                  <p className="rounded-lg bg-sky-50 border border-sky-200 text-sky-900 text-[11px] font-medium px-3 py-2 mb-2" data-testid="transport-docs-notice-signup">
                    {t("transportDocsNotice")}
                  </p>
                )}
                {form.categories.map((c) => (
                  <div key={c} className="mb-3" data-testid={`signup-activities-${c}`}>
                    <p className="text-xs font-bold text-zinc-700 mb-1.5">{tCat(c)}</p>
                    <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pe-1">
                      {(FULL_ACTIVITIES[c] || []).map((a) => {
                        const sel = form.activities.includes(a);
                        const isVet = VET_ACTIVITIES.includes(a);
                        return (
                          <button
                            type="button"
                            key={a}
                            data-testid={`signup-act-${a}`}
                            onClick={() => toggleActivity(a)}
                            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                              sel
                                ? isVet ? "border-amber-500 bg-amber-100 text-amber-900" : "border-emerald-600 bg-emerald-600 text-white"
                                : "border-zinc-200 bg-white text-zinc-700 hover:border-emerald-300"
                            }`}
                          >
                            {tAct(a)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Provider type (optional) */}
            <div>
              <label className="label-caps mb-1.5 block">{t("merchantType")} — {t("optional")}</label>
              <select
                data-testid="signup-merchant-type"
                value={form.merchant_type}
                onChange={(e) => setForm({ ...form, merchant_type: e.target.value })}
                className={inputCls.replace("px-4", "px-3")}
              >
                <option value="">{t("selectMerchantType")}</option>
                {MERCHANT_TYPES.map((m) => <option key={m} value={m}>{tAct(m)}</option>)}
              </select>
            </div>

            {/* Coverage */}
            <div data-testid="signup-coverage-section">
              <label className="label-caps mb-2 block">{t("coverageTitle")}</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  data-testid="signup-coverage-full"
                  onClick={() => setForm({ ...form, coverage: "full_city", districts: [] })}
                  className={`rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${form.coverage === "full_city" ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 bg-white text-zinc-700"}`}
                >{t("fullCity")}</button>
                <button
                  type="button"
                  data-testid="signup-coverage-districts"
                  disabled={!form.city || cityDistricts.length === 0}
                  onClick={() => setForm({ ...form, coverage: "districts" })}
                  className={`rounded-xl border px-3 py-2.5 text-xs font-semibold transition disabled:opacity-50 ${form.coverage === "districts" ? "border-emerald-600 bg-emerald-50 text-emerald-700" : "border-zinc-200 bg-white text-zinc-700"}`}
                >{t("specificDistricts")}</button>
              </div>
              {form.coverage === "districts" && cityDistricts.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="signup-district-picker">
                  {cityDistricts.map((d) => {
                    const sel = form.districts.includes(d);
                    return (
                      <button
                        type="button"
                        key={d}
                        data-testid={`signup-district-${d}`}
                        onClick={() => toggleDistrict(d)}
                        className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${sel ? "border-emerald-600 bg-emerald-600 text-white" : "border-zinc-200 bg-white text-zinc-700"}`}
                      >
                        {d}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <PhoneField label={t("whatsapp")} testId="signup-whatsapp" value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} required lang="en" />
            <div>
              <label className="label-caps mb-1.5 block">{t("description")}</label>
              <textarea
                data-testid="signup-description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={3}
                className="w-full rounded-xl border border-zinc-200 bg-zinc-50 focus:bg-white focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600 outline-none px-3 py-2"
              />
            </div>
          </>
        )}

        {error && <div data-testid="signup-error" role="alert" className="text-sm text-red-600 bg-red-50 rounded-lg p-3">{error}</div>}

        <button data-testid="signup-submit" disabled={!canSubmit || busy} className="btn-primary w-full">
          {busy ? t("loadingLabel") : t("signup")}
        </button>
      </form>

      {role === "customer" && (
        <>
          <div className="my-5 flex items-center gap-3">
            <div className="flex-1 h-px bg-zinc-200" />
            <span className="text-xs text-zinc-400">{t("or")}</span>
            <div className="flex-1 h-px bg-zinc-200" />
          </div>
          <GoogleButton testId="signup-google" />
        </>
      )}

      <p className="mt-6 text-center text-sm text-zinc-600">
        {t("alreadyHave")}{" "}
        <Link to="/login" data-testid="link-login" className="font-semibold text-emerald-700 hover:underline">{t("login")}</Link>
      </p>
    </div>
  );
}

function Field({ label, value, onChange, testId, type = "text" }) {
  return (
    <div>
      <label className="label-caps mb-1.5 block">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={inputCls}
      />
    </div>
  );
}

function PhoneField({ label, value, onChange, testId, required, lang }) {
  const invalid = value && !isValidSaudi(value);
  const hint = lang === "ar" ? SAUDI_HINT_AR : SAUDI_HINT_EN;
  return (
    <div>
      <label className="label-caps mb-1.5 block">{label}{required ? " *" : ""}</label>
      <input
        data-testid={testId}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="+9665XXXXXXXX"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full h-12 rounded-xl border bg-zinc-50 focus:bg-white outline-none px-4 ${invalid ? "border-rose-400 focus:border-rose-500 focus:ring-1 focus:ring-rose-500" : "border-zinc-200 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600"}`}
      />
      <div className={`text-[11px] mt-1 ${invalid ? "text-rose-600 font-medium" : "text-zinc-400"}`} data-testid={`${testId}-hint`}>{hint}</div>
    </div>
  );
}
